import "./style.scss";

import { Box } from "@mui/material";
import Plus from "assets/img/svg/plus";
import type { TImportDataDictionary } from "components/ImportData/type";
import { Spinner } from "components/shared/Spinner";
import StyledButton from "components/shared/StyledButton";
import { getImportTypeTranslation } from "containers/BulkUpdatesContainer";
import type { TPendingImport } from "containers/BulkUpdatesContainer/ConfirmationStep";
import ScopeContainer from "containers/ScopeContext";
import useBindingKeyboard from "hooks/useBindingKeyboard";
import type { ReactElement, ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IBrand } from "store/brandStore";
import type { Job } from "store/jobsStore";
import type { TFetchStatus, TParamsPagination } from "store/types";

import variables from "../../../assets/styles/_variables.module.scss";
import type { Action } from "../ButtonMenu";
import ButtonMenu from "../ButtonMenu";
import ListFilters from "../ListFilters";
import type { PopoverMenuElements } from "./Header";
import ElementListHeader from "./Header";
import type { FilterOrder } from "./sortStorage";
import { getInitialChosenOrder, normalizeElementListSortOrder, persistElementListSort } from "./sortStorage";

type TProps = {
    /**
     * contain the id of the current element (targeted) in the list.
     */
    currentElement: number;
    /**
     * set the value of currentElement. It's setted by clicking on a Title or navigate with they key-up or key-down.
     */
    setCurrentElement: (element: any) => void;
    /**
     * contain the data of all titles (array of objects)
     */
    elements: any[];
    /**
     * current status of the element fetching "idle", "pending", "succeed", it's used for pagination.
     */
    elementsStatus: TFetchStatus;
    /**
     * contain the total number of elements.
     */
    elementCount: number;
    /**
     * object containing the current label infos displayed in the header and a logic function for the add item action button.
     */
    elementData: {
        label: string;
        actionLabel: string;
        action: (action?: "create-beneficiar" | "invite-author") => void;
        actionDisabled?: boolean;
    };
    /**
     * logic fetch function for the items searching bar.
     */
    elementSearchFunc: (options: TParamsPagination) => void;
    /**
     * object containing labels, icon and logic functions for imports and exports menu.
     */
    popoverMenu?: PopoverMenuElements;
    /**
     * rendering condition to display the tags filter button.
     */
    showTags?: boolean;
    /**
     * condition to change the style of the item list depending on whether this item is being edited.
     */
    editMode?: boolean;
    children: ReactElement;
    /**
     * list of tags for the tags filter option. (array of objects)
     */
    tagList?: any;
    /**
     * See if company uses brands or not.
     */
    usesBrands?: boolean;
    /**
     * All brands from company
     */
    brands?: IBrand[];
    /**
     * Name of the company
     */
    companyName?: string;
    orderOptions: { name: string; label: string }[];
    /**
     * Boolean to show popover choice on click on the create btn
     */
    hasMultipleActionBtn?: boolean;
    /**
     * Actions for create btn
     */
    btnMenuActions?: Action[];
    /**
     * Verifiy if the search fetch request is load for the first time. Check if reqStatus === "idle"
     */
    isInitialLoad?: boolean;
    /**
     * Same list-item components with text skeletons, shown while the list fetches.
     */
    listSkeleton?: ReactNode;
    /**
     * When true, list skeleton is shown until the page list is ready (e.g. during refresh).
     */
    showListLoading?: boolean;
    /**
     * Object with starting default options if different from State below
     */
    defaultFilterOptions?: FilterOrder;
    /**
     * When set, sort choice is restored from and saved to localStorage under this key.
     */
    sortStorageKey?: string;
    /**
     * Show a filter to list titles without any term.
     */
    showHasTermFilter?: boolean;
    pendingJobs?: TPendingImport[] | Job[];
    /**
     * Bump this value to replay the search with the filters currently applied (query, sort, tags, brand).
     */
    refreshKey?: number;
    /**
     * Extra buttons in the header, left of the imports / exports menu button (e.g. a view toggle)
     */
    headerActions?: ReactElement;
};

export const whiteButton = {
    "&.MuiButton-root": {
        color: "black",
        backgroundColor: "white",
        border: `1px solid ${variables["border-grey"]} !important`,
        borderRadius: variables["border-radius"],
        padding: 0,
    },
};

/**
 * ElementList is a component list of elements.
 * It shows the list of Titles, Beneficiars, and Publications.
 * This component includes a child component depending on the page :
 * - TitleListItem
 * - BeneficiarListItem
 * - PublicationListItem
 * @param currentElement contain the id of the current element (targeted) in the list.
 * @param setCurrentElement set the value of currentElement. It's setted by clicking on a Title or navigate with they key-up or key-down.
 * @param elements contain the data of all titles (array of objects)
 * @param elementsStatus current status of the element fetching "idle", "pending", "succeed", it's used for pagination.
 * @param elementCount contain the total number of elements.
 * @param elementData object containing the current label infos displayed in the header and a logic function for the add item action button.
 * @param elementSearchFunc logic fetch function for the items searching bar.
 * @param popoverMenu object containing labels, icon and logic functions for imports and exports menu.
 * @param showTags rendering condition to display the tags filter button.
 * @param editMode condition to change the style of the item list depending on whether this item is being edited.
 * @param tagList list of tags for the tag filter option. (array of objects)
 * @param usesBrands boolean to know if company uses brands
 * @param brands all brands from company
 * @param companyName company name (for the brand filter)
 * @param orderOptions array with options for order filter
 * @param hasMultipleActionBtn boolean to show popover choice on click on the create btn
 * @param btnMenuActions Actions for create btn
 * @param isInitialLoad Verifiy if the search fetch request is load for the first time. Check if reqStatus === "idle"
 * @param defaultFilterOptions (Optional) object with starting default options if different from State below
 * @param sortStorageKey (Optional) localStorage key to persist sort preferences
 * @param refreshKey (Optional) bump to replay the search with the filters currently applied
 */

const ElementList = (props: TProps) => {
    const {
        currentElement,
        setCurrentElement,
        elements,
        elementsStatus,
        elementCount,
        elementData,
        elementSearchFunc,
        popoverMenu = undefined,
        showTags = false,
        editMode = false,
        children,
        tagList,
        usesBrands,
        brands,
        companyName,
        orderOptions,
        hasMultipleActionBtn = false,
        btnMenuActions,
        isInitialLoad = false,
        listSkeleton,
        showListLoading = false,
        defaultFilterOptions,
        sortStorageKey,
        showHasTermFilter = false,
        pendingJobs = [],
        refreshKey = 0,
        headerActions,
    } = props;

    elementData.actionDisabled = elementData.actionDisabled ?? false;

    const [t] = useTranslation();

    const [chosenTags, setChosenTags] = useState<string[]>([]);

    const [chosenOrder, setChosenOrder] = useState<FilterOrder>(() =>
        getInitialChosenOrder(
            sortStorageKey,
            orderOptions.map((o) => o.name),
            defaultFilterOptions,
        ),
    );
    const [chosenBrand, setChosenBrand] = useState<string>("");
    const [withoutTermOnly, setWithoutTermOnly] = useState<boolean>(false);

    const [positionScroll, setPositionScroll] = useState<number>(0);
    const [query, setQuery] = useState<string>("");
    const [timeOut, setTimeOut] = useState<NodeJS.Timeout | undefined>();
    const [filterTimeOut, setFilterTimeOut] = useState<NodeJS.Timeout | undefined>();
    const [queryIsTyping, setQueryIsTyping] = useState<boolean | null>(null);
    const [isFiltering, setIsFiltering] = useState<boolean | null>(null);
    const isMounted = useRef(false);
    const refList = useRef<HTMLDivElement>(null);
    const elementListRef = useRef(null);
    const queryRef = useRef(query);
    queryRef.current = query;

    const handleRequest = (offset?: number) => {
        const options: TParamsPagination = { limit: 100 };
        offset && (options.offset = offset);
        const currentQuery = queryRef.current.trim();
        if (currentQuery) options.query = currentQuery;
        if (chosenOrder) {
            options.sortBy = chosenOrder.sortBy;
            options.sortOrder = chosenOrder.sortOrder;
        }
        chosenTags.length && (options.tags = chosenTags);
        chosenBrand !== "" && (options.brand = chosenBrand);
        withoutTermOnly && (options.hasTerm = "0");
        elementSearchFunc(options);
    };

    const handleQuery = (value: string) => {
        setQuery(value);
        queryRef.current = value;
        if (value === "") {
            if (timeOut) clearTimeout(timeOut);
            setQueryIsTyping(false);
            if (!isInitialLoad) handleRequest();
            return;
        }
        if (!queryIsTyping) {
            setTimeOut(
                setTimeout(() => {
                    setQueryIsTyping(false);
                }, 700),
            );
            setQueryIsTyping(true);
        } else {
            clearTimeout(timeOut);
            setTimeOut(
                setTimeout(() => {
                    setQueryIsTyping(false);
                }, 700),
            );
        }
    };

    const handleFilter = () => {
        if (!isFiltering) {
            setFilterTimeOut(
                setTimeout(() => {
                    setIsFiltering(false);
                }, 700),
            );
            setIsFiltering(true);
        } else {
            clearTimeout(filterTimeOut);
            setFilterTimeOut(
                setTimeout(() => {
                    setIsFiltering(false);
                }, 700),
            );
        }
    };

    const handleOrderSelection = (order: FilterOrder) => {
        const normalized = normalizeElementListSortOrder(order);
        setChosenOrder(normalized);
        if (sortStorageKey) persistElementListSort(sortStorageKey, normalized);
        handleFilter();
    };

    const handleScroll = (ev: any) => {
        const target = ev.target as HTMLElement;
        setPositionScroll(
            (target.offsetHeight * 100) / target.scrollHeight + (target.scrollTop * 100) / target.scrollHeight,
        );
    };

    const handleKeyArrow = useCallback(
        (ev: KeyboardEvent) => {
            const elm = elements.find((element) => element.id === currentElement);
            if (
                elm &&
                elements.indexOf(elm) > -1 &&
                ((ev.code === "ArrowDown" && elements.indexOf(elm) < elements.length - 1) ||
                    (ev.code === "ArrowUp" && elements.indexOf(elm) > 0))
            ) {
                setCurrentElement(elements[elements.indexOf(elm) + (ev.code === "ArrowDown" ? 1 : -1)]);
                const activeElement: HTMLElement | null | undefined = refList.current?.querySelector(".active");
                if (!!refList?.current && !!activeElement) {
                    if (
                        ev.code === "ArrowDown" &&
                        activeElement.offsetTop -
                            refList.current.offsetTop +
                            activeElement.offsetHeight * 2 -
                            refList.current.scrollTop >
                            refList.current.clientHeight
                    ) {
                        refList.current.scrollTop =
                            (elements.indexOf(elm) + 2) * (activeElement as HTMLElement).offsetHeight -
                            refList.current.clientHeight;
                    } else if (
                        ev.code === "ArrowUp" &&
                        activeElement.offsetTop -
                            refList.current.offsetTop -
                            activeElement.offsetHeight -
                            refList.current.scrollTop <
                            0
                    ) {
                        refList.current.scrollTop =
                            (elements.indexOf(elm) - 1) * (activeElement as HTMLElement).offsetHeight;
                    }
                }
            } else {
                setCurrentElement(elements[0]);
                if (refList?.current) {
                    refList.current.scrollTop = 0;
                }
            }
        },
        [elements, currentElement, setCurrentElement],
    );

    useBindingKeyboard({
        action: handleKeyArrow,
        type: "keydown",
        touchToBind: ["ArrowUp", "ArrowDown"],
    });

    useEffect(() => {
        if (queryIsTyping !== false && isFiltering !== false) return;
        if (isInitialLoad) return;
        handleRequest();
    }, [queryIsTyping, isFiltering]);

    useEffect(() => {
        if (!refreshKey) return;
        handleRequest();
    }, [refreshKey]);

    useEffect(() => {
        if (elementsStatus !== "pending" && positionScroll > 70 && elements.length < elementCount) {
            handleRequest(elements.length);
        }
    }, [positionScroll]);

    useEffect(() => {
        if (isMounted.current) {
            handleRequest();
        }
    }, []);

    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
            return;
        }
    }, [isMounted]);

    const showLoading = showListLoading || isInitialLoad || (elementsStatus === "pending" && positionScroll === 0);

    const noPendingJobs = (pendingJobs ?? []).length === 0;

    return (
        <div style={{ minWidth: 335 }} ref={elementListRef}>
            <div className="elements-list-container">
                <div className="elements-list-head" style={editMode ? { opacity: 0.1, pointerEvents: "none" } : {}}>
                    <ElementListHeader
                        label={elementData.label}
                        count={elementCount}
                        loading={showLoading && elementCount === 0}
                        headerActions={headerActions}
                        popoverMenu={popoverMenu}
                        pendingJobs={pendingJobs}
                        keyNode={elementListRef.current}
                    />
                    <div className="elements-list-actions">
                        <div className="elements-list-actions-left">
                            {hasMultipleActionBtn && btnMenuActions ? (
                                <ScopeContainer
                                    errorProps={{ disabled: true }}
                                    extraConditions={[noPendingJobs, !elementData.actionDisabled]}
                                >
                                    {(props) => (
                                        <ButtonMenu
                                            mainActions={{ icon: <Plus />, name: elementData.actionLabel }}
                                            actions={btnMenuActions}
                                            showExpandBtn={false}
                                            fullWidth
                                            tooltipText={
                                                !noPendingJobs
                                                    ? t("AsyncUploads.PendingTooltip", {
                                                          element: getImportTypeTranslation(
                                                              pendingJobs[0].importType as TImportDataDictionary,
                                                          ),
                                                      })
                                                    : elementData.actionDisabled
                                                      ? t("LogIn.NotAuthorizedException")
                                                      : ""
                                            }
                                            {...props}
                                        />
                                    )}
                                </ScopeContainer>
                            ) : (
                                <ScopeContainer
                                    errorProps={{ disabled: true }}
                                    extraConditions={[noPendingJobs, !elementData.actionDisabled]}
                                >
                                    {(props) => (
                                        <StyledButton
                                            fullWidth
                                            buttonType="primary"
                                            onClick={() => elementData.action()}
                                            tooltipText={
                                                !noPendingJobs
                                                    ? t("AsyncUploads.PendingTooltip", {
                                                          element: getImportTypeTranslation(
                                                              pendingJobs[0].importType as TImportDataDictionary,
                                                          ),
                                                      })
                                                    : elementData.actionDisabled
                                                      ? t("LogIn.NotAuthorizedException")
                                                      : ""
                                            }
                                            {...props}
                                        >
                                            <Plus />
                                            {elementData.actionLabel}
                                        </StyledButton>
                                    )}
                                </ScopeContainer>
                            )}
                        </div>
                    </div>
                    <ListFilters
                        searchValue={query}
                        onSearchValue={handleQuery}
                        orderOptions={orderOptions}
                        onOrderSelection={handleOrderSelection}
                        chosenOrder={chosenOrder}
                        showTagFilter={showTags}
                        tags={tagList}
                        chosenTags={chosenTags}
                        // I know typing "any" here isn't the best but I'll get back to it later
                        setChosenTags={(value: any) => {
                            setChosenTags(value);
                            handleFilter();
                        }}
                        usesBrands={usesBrands}
                        chosenBrand={chosenBrand}
                        setChosenBrand={(brand: any) => {
                            setChosenBrand(brand);
                            handleFilter();
                        }}
                        brands={brands}
                        companyName={companyName}
                        showHasTermFilter={showHasTermFilter}
                        withoutTermOnly={withoutTermOnly}
                        setWithoutTermOnly={(value: boolean) => {
                            setWithoutTermOnly(value);
                            handleFilter();
                        }}
                    />
                </div>
                <div
                    className="elements-list"
                    onScroll={(ev) => handleScroll(ev)}
                    style={editMode ? { overflow: "hidden" } : {}}
                    ref={refList}
                >
                    {showLoading && listSkeleton ? (
                        listSkeleton
                    ) : showLoading ? (
                        <Box
                            sx={{
                                display: "flex",
                                width: "100%",
                                height: "100%",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <Spinner />
                        </Box>
                    ) : (
                        children
                    )}
                </div>
            </div>
        </div>
    );
};

export default ElementList;
